import { EditorOnly } from '../components/EditorOnly';
import { Icon } from '@iconify/react';
import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from '@mui/material';
import { FormEvent, useEffect, useState } from 'react';
import { api } from '../api/client';
import type { ShortsDraft } from '../api/types';
import { EmptyState } from '../components/EmptyState';
import { Box, Stack, Typography } from '../components/mui-system';
import { PageHeader } from '../components/PageHeader';
import { useWorkspace } from '../workspace-context';

const columns: Array<{ status: ShortsDraft['status']; label: string; color: string }> = [
  { status: 'idea', label: 'Идеи', color: '#ff5b83' },
  { status: 'script', label: 'Сценарий', color: '#79c7ff' },
  { status: 'ready', label: 'Готово', color: '#f1b663' },
  { status: 'published', label: 'Опубликовано', color: '#72d6a2' },
];

export function ShortsPage() {
  const { activeWorkspace } = useWorkspace();
  const [drafts, setDrafts] = useState<ShortsDraft[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    api
      .shorts(activeWorkspace!.id)
      .then(setDrafts)
      .catch((caught) =>
        setError(caught instanceof Error ? caught.message : 'Не удалось загрузить шортсы'),
      );
  }, [activeWorkspace]);

  async function create(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    try {
      const draft = await api.createShort(activeWorkspace!.id, {
        title: String(data.get('title')),
        platform: String(data.get('platform')) as ShortsDraft['platform'],
        hook: String(data.get('hook')),
        durationSeconds: Number(data.get('durationSeconds')),
      });
      setDrafts((current) => [draft, ...current]);
      setDialogOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось создать идею');
    }
  }

  async function advance(draft: ShortsDraft) {
    const index = columns.findIndex((column) => column.status === draft.status);
    const next = columns[index + 1];
    if (!next) return;
    try {
      const updated = await api.updateShort(activeWorkspace!.id, draft.id, {
        title: draft.title,
        status: next.status,
        platform: draft.platform,
        hook: draft.hook,
        script: draft.script,
        durationSeconds: draft.durationSeconds,
      });
      setDrafts((current) => current.map((item) => (item.id === updated.id ? updated : item)));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Не удалось переместить карточку');
    }
  }

  return (
    <>
      <PageHeader
        title="Шортсы"
        description="Проводите короткие видео от сильного хука до публикации, не теряя SEO-интент и сценарий."
        action={
          <EditorOnly permissions="shorts.create">
            <Button
              variant="contained"
              onClick={() => setDialogOpen(true)}
              startIcon={<Icon icon="solar:videocamera-add-linear" />}
            >
              Новая идея
            </Button>
          </EditorOnly>
        }
      />
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {!drafts.length ? (
        <EmptyState
          icon={<Icon icon="solar:video-frame-play-horizontal-linear" />}
          title="Пайплайн пуст"
          description="Добавьте тему и хук для первого короткого видео — карточка появится в колонке идей."
          action={
            <EditorOnly permissions="shorts.create">
              <Button variant="contained" onClick={() => setDialogOpen(true)}>
                Добавить идею
              </Button>
            </EditorOnly>
          }
        />
      ) : (
        <Box
          display="grid"
          gridTemplateColumns={{ xs: 'repeat(4, minmax(280px, 1fr))', xl: 'repeat(4, 1fr)' }}
          gap={1.4}
          sx={{ overflowX: 'auto', pb: 1.5 }}
        >
          {columns.map((column) => {
            const items = drafts.filter((draft) => draft.status === column.status);
            return (
              <Box
                key={column.status}
                sx={{
                  minWidth: 280,
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2.5,
                  p: 1.1,
                  alignSelf: 'start',
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  p={0.7}
                  mb={0.7}
                >
                  <Stack direction="row" gap={1} alignItems="center">
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: 99,
                        bgcolor: column.color,
                        boxShadow: `0 0 0 4px ${column.color}33`,
                      }}
                    />
                    <Typography fontWeight={800}>{column.label}</Typography>
                  </Stack>
                  <Chip label={items.length} size="small" />
                </Stack>
                <Stack gap={0.9}>
                  {items.map((draft) => (
                    <ShortCard
                      key={draft.id}
                      draft={draft}
                      canAdvance={column.status !== 'published'}
                      onAdvance={() => void advance(draft)}
                    />
                  ))}
                  {!items.length && (
                    <Box py={4} textAlign="center">
                      <Typography color="text.secondary" fontSize={12}>
                        Карточек нет
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Box>
      )}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={create}>
          <DialogTitle>Новая идея для Shorts</DialogTitle>
          <DialogContent>
            <Stack gap={2} pt={1}>
              <TextField
                name="title"
                label="Тема"
                required
                autoFocus
                placeholder="3 ошибки SEO-аудита"
              />
              <TextField
                name="hook"
                label="Хук первых секунд"
                multiline
                minRows={2}
                placeholder="Ваш сайт теряет трафик из-за одной незаметной настройки…"
              />
              <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                <TextField
                  name="platform"
                  label="Платформа"
                  select
                  defaultValue="youtube"
                  fullWidth
                >
                  <MenuItem value="youtube">YouTube</MenuItem>
                  <MenuItem value="instagram">Instagram</MenuItem>
                  <MenuItem value="tiktok">TikTok</MenuItem>
                </TextField>
                <TextField
                  name="durationSeconds"
                  label="Длительность, сек."
                  type="number"
                  defaultValue={30}
                  fullWidth
                  slotProps={{ htmlInput: { min: 5, max: 180 } }}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Отмена</Button>
            <Button type="submit" variant="contained">
              Добавить
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </>
  );
}

function ShortCard({
  draft,
  canAdvance,
  onAdvance,
}: {
  draft: ShortsDraft;
  canAdvance: boolean;
  onAdvance(): void;
}) {
  const platformIcon =
    draft.platform === 'youtube'
      ? 'logos:youtube-icon'
      : draft.platform === 'instagram'
        ? 'skill-icons:instagram'
        : 'logos:tiktok-icon';
  return (
    <Card>
      <CardContent sx={{ p: 1.7 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.2}>
          <Icon icon={platformIcon} width={20} />
          <Typography fontSize={11} color="text.secondary">
            {draft.durationSeconds} сек.
          </Typography>
        </Stack>
        <Typography fontWeight={780} lineHeight={1.32}>
          {draft.title}
        </Typography>
        {draft.hook && (
          <Typography
            fontSize={12}
            color="text.secondary"
            mt={0.9}
            sx={{
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {draft.hook}
          </Typography>
        )}
        {canAdvance && (
          <EditorOnly
            permissions={
              draft.status === 'ready' ? ['shorts.edit', 'shorts.publish'] : 'shorts.edit'
            }
          >
            <Button
              size="small"
              onClick={onAdvance}
              endIcon={<Icon icon="solar:arrow-right-linear" />}
              sx={{ mt: 1.4, px: 0, '&:hover': { bgcolor: 'transparent' } }}
            >
              Следующий этап
            </Button>
          </EditorOnly>
        )}
      </CardContent>
    </Card>
  );
}
